/**
 * HistoryBadge.jsx — Bundle U-9.G — "Loaded from history" chip
 * ============================================================================
 *
 * OPERATOR-LOCKED. Small inline pill rendered next to a workspace
 * section's title when the bound result was loaded via the mount-
 * time discovery hook (`useWorkspaceArtefactDiscovery`) rather than
 * via a user-initiated dispatch in this session.
 *
 * Differentiates the two cases visually so the user is never
 * surprised by a section that "is already filled in" — eliminating
 * the pre-U-9 confusion where the section appeared to be a fresh
 * generation even though it was a history bind.
 *
 * Strict-rail:
 *   • Render-only — zero side effects, zero state.
 *   • Uses Tailwind utility classes consistent with the workspace
 *     amber/info palette (matches the U-3.IP / U-5.CV.A confirm
 *     dialogs).
 *   • Accessible: role="status" + aria-label so screen readers
 *     announce "Loaded from history" once per render.
 */

import React from 'react';
import { History } from 'lucide-react';

import { cn } from '@/lib/utils';

export function HistoryBadge({ className, testId }) {
  return (
    <span
      role="status"
      aria-label="Loaded from history"
      data-testid={testId || 'workspace-loaded-from-history-badge'}
      className={cn(
        'inline-flex items-center gap-1 rounded-full',
        'border border-amber-500/40 bg-amber-500/10',
        'px-2 py-0.5 text-[10px] font-medium uppercase',
        'tracking-wide text-amber-200',
        className,
      )}
    >
      <History className="h-3 w-3" aria-hidden="true" />
      <span>Loaded from history</span>
    </span>
  );
}

export default HistoryBadge;
